import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useApiQuery } from '@/hooks/useApiQuery'
import { useAuth } from '@/hooks/useAuth'
import { billingApi } from '@/services/api/domains/billing.api'
import { getAuthConfig } from '@/services/auth/config'

/**
 * Compact header pill showing the tenant's available credit balance.
 *
 * Self-gating: renders `null` on on-prem (Keycloak), before auth config has
 * loaded (getAuthConfig throws), or while the balance query is loading / errored
 * / empty. Admins get a clickable button → /settings/billing; everyone else
 * sees plain informational text.
 */
export function CreditBalanceIndicator() {
  const { t } = useTranslation('console')
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data, isLoading, error } = useApiQuery(() => billingApi.getBalance())

  // Cloud-only. getAuthConfig() throws before loadAuthConfig() resolves — treat
  // that (and on-prem) as "don't show".
  let isCloud = false

  try {
    isCloud = getAuthConfig().provider !== 'keycloak'
  } catch {
    isCloud = false
  }

  if (!isCloud || !user?.tenantId || isLoading || error || !data) return null

  const credits = Math.floor(data.availableCredits)
  const label = t('billing.balance.credits', 'credits')
  const isAdmin = user?.role === 'admin'

  const content = (
    <>
      <span className="font-semibold tabular-nums">{credits.toLocaleString()}</span>
      <span className="text-emerald-700/80 dark:text-emerald-200/80">{label}</span>
    </>
  )

  const baseClass =
    'inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100'

  if (!isAdmin) {
    return (
      <span className={baseClass} aria-label={`${credits} ${label}`}>
        {content}
      </span>
    )
  }

  return (
    <button
      type="button"
      onClick={() => navigate('/settings/billing')}
      className={`${baseClass} transition-colors hover:bg-emerald-100 dark:hover:bg-emerald-900/60`}
      aria-label={`${credits} ${label}`}
    >
      {content}
    </button>
  )
}
